import { useRef } from 'react';
import { ArrowUpRight, GithubLogo, MapPin } from '@phosphor-icons/react';
import { SECTION_IDS, site } from '../content/site';
import { gsap, useGSAP, EASE, MOTION_OK } from '../lib/gsap';
import { ContactForm } from '../components/contact/ContactForm';
import { revealLines } from '../components/vision/lineReveal';
import { HALO } from '../components/vision/halo';
import { glue, noWidow } from '../components/vision/glue';

const LINK =
	'inline-flex min-h-11 items-center gap-2.5 text-[15px] font-medium text-mineral decoration-solar/70 decoration-1 underline-offset-[5px] transition-colors duration-300 ease-expo hover:underline';

/**
 * Contact: the conversion moment, on Forest Night (the dusk chapter carries on).
 * Desktop: pitch and direct contact on the left, the form panel on the right.
 * Below 1024px everything stacks in reading order.
 */
export function Contact() {
	const ref = useRef<HTMLElement>(null);
	const titleRef = useRef<HTMLHeadingElement>(null);
	const { title, text, directLabel, githubLabel } = site.contact;
	const { email, github, location } = site.person;

	useGSAP(
		() => {
			const section = ref.current;
			const heading = titleRef.current;
			if (!section || !heading) return;
			const mm = gsap.matchMedia();

			mm.add(MOTION_OK, (context) => {
				const cancelTitle = revealLines(heading, context, { start: 'top 80%' });

				const pitch = section.querySelectorAll('[data-contact-fade]');
				const panel = section.querySelector('[data-contact-panel]');

				gsap.from(pitch, {
					y: 24,
					opacity: 0,
					duration: 1.1,
					ease: EASE,
					stagger: 0.1,
					delay: 0.25,
					scrollTrigger: { trigger: heading, start: 'top 80%' },
				});
				if (panel) {
					gsap.from(panel, {
						y: 40,
						opacity: 0,
						duration: 1.2,
						ease: EASE,
						scrollTrigger: { trigger: panel, start: 'top 85%' },
					});
				}

				return cancelTitle;
			});
		},
		{ scope: ref },
	);

	return (
		<section
			id={SECTION_IDS.contact}
			ref={ref}
			aria-labelledby="contact-title"
			className="section-pad relative text-mineral"
		>
			<div className="container-site grid gap-y-16 lg:grid-cols-12 lg:gap-x-6">
				<div className="lg:col-span-5">
					<h2
						id="contact-title"
						ref={titleRef}
						className="text-balance font-display text-[clamp(2.5rem,5vw,5.5rem)] font-bold leading-[0.98] tracking-[-0.035em] text-mineral"
					>
						{noWidow(title)}
					</h2>
					<p
						data-contact-fade
						className={`mt-8 max-w-[40ch] text-[clamp(1.0625rem,1.3vw,1.1875rem)] leading-[1.6] text-dusk-muted ${HALO}`}
					>
						{glue(text)}
					</p>

					<div data-contact-fade className={`mt-12 border-t border-mineral/10 pt-8 md:mt-16 ${HALO}`}>
						<p className="text-[15px] text-dusk-muted">{directLabel}</p>
						<a
							href={`mailto:${email}`}
							className="mt-2 inline-flex min-h-11 items-center break-all font-display text-[clamp(1.625rem,2.5vw,2.375rem)] font-medium leading-tight tracking-[-0.02em] text-mineral decoration-solar decoration-2 underline-offset-[0.2em] hover:underline"
						>
							{email}
						</a>

						<ul className="mt-6 flex flex-wrap gap-x-8">
							<li>
								<a href={github} target="_blank" rel="noopener noreferrer" className={LINK}>
									<GithubLogo aria-hidden size={18} weight="regular" className="text-solar" />
									{githubLabel}
									<ArrowUpRight aria-hidden size={14} weight="bold" className="text-dusk-muted" />
								</a>
							</li>
						</ul>

						<p className="mt-4 flex items-center gap-2.5 text-[15px] text-dusk-muted">
							<MapPin aria-hidden size={18} weight="regular" className="shrink-0" />
							{location}
						</p>
					</div>
				</div>

				<div className="lg:col-span-6 lg:col-start-7">
					<ContactForm />
				</div>
			</div>
		</section>
	);
}
